import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import type { Article, NewsItem } from "../../../shared/news";
import { api } from "../../data/api";
import { Icon } from "../../ui/Icon";
import { openOutside, QrCode } from "../../ui/OutsideLink";
import { Sheet } from "../../ui/Sheet";
import { NewsImage, SourceBadge, storyAge } from "./newsUi";

/**
 * A story read right here: Kinmantle fetches the page and keeps just the story's text
 * (shared/news.ts readArticle). When a page has no readable story (a paywall, a video,
 * a publisher that turns Kinmantle away), the QR code to open it on a phone is shown instead.
 */
export function ArticleSheet({ story, resort, onClose }: { story: NewsItem & { url: string }; /** A story kept for this mountain on the Resorts screen. */ resort?: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["news", "article", story.url],
    queryFn: ({ signal }) =>
      api<Article>(`/news/article?source=${encodeURIComponent(story.source)}&u=${encodeURIComponent(story.url)}${resort ? `&resort=${encodeURIComponent(resort)}` : ""}`, { signal }),
    staleTime: 60 * 60_000,
    retry: 1,
  });
  const a = q.data;
  const readable = Boolean(a?.paragraphs.length);
  const failed = q.isError || (a && !readable);
  const age = storyAge(story);

  return (
    <Sheet
      title={<SourceBadge id={story.source} />}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {readable && (
            <button className="btn" onClick={() => openOutside(story.url, story.title)}>
              <Icon name="phone" size="1.1rem" /> Open on your phone
            </button>
          )}
          <span className="spacer" />
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <article className="article">
        <NewsImage src={a?.image ?? story.image} className="article-img" />
        <h3 className="article-title">{story.title}</h3>
        <p className="article-meta">
          {[a?.byline && `By ${a.byline.replace(/^by\s+/i, "")}`, age].filter(Boolean).join(" · ")}
        </p>
        {readable ? (
          a!.paragraphs.map((p, i) => <p key={i}>{p}</p>)
        ) : (
          <>
            {story.summary && <p className="article-summary">{story.summary}</p>}
            {failed ? (
              <div className="article-fallback">
                <p className="muted">This story can’t be shown here. Point your phone’s camera at the code to read it there.</p>
                <QrCode text={story.url} />
              </div>
            ) : (
              <p className="article-loading muted">Loading the story…</p>
            )}
          </>
        )}
      </article>
    </Sheet>
  );
}

/** A story row or card that opens the story in the app. */
export function ArticleLink({ story, className, ariaLabel, children }: { story: NewsItem; className?: string; ariaLabel?: string; children: ReactNode }) {
  // The story as tapped: a rotating top story moves on, the open one doesn't.
  const [open, setOpen] = useState<(NewsItem & { url: string }) | null>(null);
  if (!story.url) return <div className={className}>{children}</div>;
  const url = story.url;
  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setOpen({ ...story, url });
        }}
      >
        {children}
      </button>
      {open && <ArticleSheet story={open} onClose={() => setOpen(null)} />}
    </>
  );
}
